import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import FunnelLayout from "../FunnelLayout";
import { useFunnelForm } from "../useFunnelForm";
import { updateStep4 } from "../api";
import { readTrustedFormCert } from "../trustedform";
import { fireConversion } from "../gtag";
import { fireLead } from "../fbpixel";

function UserIcon({ size = 20 }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="8" r="4" />
      <path d="M4 22a8 8 0 0116 0" />
    </svg>
  );
}

function PhoneIcon({ size = 20 }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81 19.79 19.79 0 01.01 1.18 2 2 0 012 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 14.92v2z" />
    </svg>
  );
}

function MailIcon({ size = 20 }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <polyline points="2 7 12 14 22 7" />
    </svg>
  );
}

function LocationPinIcon({ size = 20 }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}

function ShieldLockIcon({ size = 22 }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <rect x="9.5" y="11" width="5" height="4.5" rx="0.8" />
      <path d="M10.5 11V9.5a1.5 1.5 0 013 0V11" />
    </svg>
  );
}

function UserShieldIcon({ size = 22 }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <circle cx="12" cy="10" r="2" />
      <path d="M9 16c0-1.5 1.5-3 3-3s3 1.5 3 3" />
    </svg>
  );
}

function ArrowRightIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0110 0v4" />
    </svg>
  );
}

function formatPhone(raw) {
  const digits = raw.replace(/\D/g, "").slice(0, 10);
  if (digits.length === 0) return "";
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

const NAME_RE = /^[A-Za-z\s'-]+$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const SEQUENTIAL_PHONES = new Set(["1234567890", "0123456789"]);

function validateName(val, fieldLabel) {
  const trimmed = val.trim();
  if (!trimmed) return `Please enter your ${fieldLabel}.`;
  if (trimmed.length > 50 || !NAME_RE.test(trimmed)) {
    return `Please enter a valid ${fieldLabel}.`;
  }
  return null;
}

function validatePhone(display) {
  const digits = display.replace(/\D/g, "");
  if (!digits) return "Please enter your phone number.";
  if (digits.length !== 10) return "Please enter a valid 10-digit US phone number.";
  if (/^(\d)\1{9}$/.test(digits)) return "Please enter a valid 10-digit US phone number.";
  if (SEQUENTIAL_PHONES.has(digits)) return "Please enter a valid 10-digit US phone number.";
  if (digits[0] === "0" || digits[0] === "1") return "Please enter a valid 10-digit US phone number.";
  return null;
}

function validateEmail(val) {
  const trimmed = val.trim();
  if (!trimmed) return "Please enter your email address.";
  if (!EMAIL_RE.test(trimmed)) return "Please enter a valid email address.";
  return null;
}

function validateZip(val) {
  if (!val) return "Please enter your ZIP code.";
  if (!/^\d{5}$/.test(val)) return "ZIP code must be 5 digits.";
  return null;
}

function Field({ id, label, icon, error, children }) {
  return (
    <div id={`fv2-field-${id}`} className="fv2-step4-field">
      <label htmlFor={`fv2-input-${id}`} className="fv2-step4-flabel">
        {label}
        <span className="fv2-step4-required" aria-hidden="true">*</span>
      </label>
      <div
        className={
          error
            ? "fv2-step4-input-wrap fv2-step4-input-wrap-error"
            : "fv2-step4-input-wrap"
        }
      >
        <span className="fv2-step4-input-icon">{icon}</span>
        {children}
      </div>
      {error && (
        <p className="fv2-step4-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

export default function Step4() {
  const navigate = useNavigate();
  const { data, update } = useFunnelForm();

  const [firstName, setFirstName] = useState(data.step4?.firstName || "");
  const [lastName, setLastName] = useState(data.step4?.lastName || "");
  const [phoneDisplay, setPhoneDisplay] = useState(
    data.step4?.phoneDisplay || ""
  );
  const [email, setEmail] = useState(data.step4?.email || "");
  const [zip, setZip] = useState(
    data.step4?.zip || data.step1?.zip || ""
  );
  const [smsOptIn, setSmsOptIn] = useState(
    data.step4?.smsOptIn || false
  );
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState("");

  const clearError = (key) => {
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const next = {};
    const fnErr = validateName(firstName, "first name");
    const lnErr = validateName(lastName, "last name");
    const phErr = validatePhone(phoneDisplay);
    const emErr = validateEmail(email);
    const zpErr = validateZip(zip);
    if (fnErr) next.firstName = fnErr;
    if (lnErr) next.lastName = lnErr;
    if (phErr) next.phone = phErr;
    if (emErr) next.email = emErr;
    if (zpErr) next.zip = zpErr;

    setErrors(next);

    if (Object.keys(next).length > 0) {
      const order = ["firstName", "lastName", "phone", "email", "zip"];
      const firstKey = order.find((k) => next[k]);
      if (firstKey) {
        const el = document.getElementById(`fv2-field-${firstKey}`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      return;
    }

    if (submitting) return;

    const step4 = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      phone: phoneDisplay.replace(/\D/g, ""),
      phoneDisplay,
      email: email.trim(),
      zip,
      smsOptIn,
      trustedFormCertUrl: readTrustedFormCert()
    };
    update("step4", step4);

    if (!data.leadId) {
      navigate("/v2/step-1");
      return;
    }

    setApiError("");
    setSubmitting(true);
    try {
      await updateStep4(data.leadId, {
        step1: data.step1,
        step2: data.step2,
        step3: data.step3,
        step4,
        createdAt: data.createdAt,
        tracking: data.tracking,
        pageUrl: window.location.href
      });
      fireConversion(data.leadId);
      fireLead(data.leadId);
      navigate("/v2/step-5");
    } catch (err) {
      setApiError("We couldn't save your info. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <FunnelLayout currentStep={4}>
      <div className="fv2-step4">
        <h1 className="fv2-step4-h1">
          <span className="fv2-step4-h1-line">Almost There — Get Your</span>
          <span className="fv2-step4-h1-line">Best Plan Options</span>
        </h1>
        <p className="fv2-step4-sub">
          Just a few more details and we'll match you with plan options in your area.
        </p>

        <form onSubmit={handleSubmit} noValidate data-tf-element-role="offer">
          <Field
            id="firstName"
            label="First name"
            icon={<UserIcon />}
            error={errors.firstName}
          >
            <input
              id="fv2-input-firstName"
              className="fv2-step4-input"
              type="text"
              value={firstName}
              onChange={(e) => {
                setFirstName(e.target.value);
                clearError("firstName");
              }}
              placeholder="Enter your first name"
              autoComplete="given-name"
              maxLength={50}
              aria-invalid={!!errors.firstName}
            />
          </Field>

          <Field
            id="lastName"
            label="Last name"
            icon={<UserIcon />}
            error={errors.lastName}
          >
            <input
              id="fv2-input-lastName"
              className="fv2-step4-input"
              type="text"
              value={lastName}
              onChange={(e) => {
                setLastName(e.target.value);
                clearError("lastName");
              }}
              placeholder="Enter your last name"
              autoComplete="family-name"
              maxLength={50}
              aria-invalid={!!errors.lastName}
            />
          </Field>

          <Field
            id="phone"
            label="Phone number"
            icon={<PhoneIcon />}
            error={errors.phone}
          >
            <input
              id="fv2-input-phone"
              className="fv2-step4-input"
              type="tel"
              value={phoneDisplay}
              onChange={(e) => {
                setPhoneDisplay(formatPhone(e.target.value));
                clearError("phone");
              }}
              placeholder="(555) 555-5555"
              autoComplete="tel"
              maxLength={14}
              aria-invalid={!!errors.phone}
            />
          </Field>

          <Field
            id="email"
            label="Email address"
            icon={<MailIcon />}
            error={errors.email}
          >
            <input
              id="fv2-input-email"
              className="fv2-step4-input"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                clearError("email");
              }}
              placeholder="Enter your email address"
              autoComplete="email"
              maxLength={120}
              aria-invalid={!!errors.email}
            />
          </Field>

          <Field
            id="zip"
            label="ZIP code"
            icon={<LocationPinIcon />}
            error={errors.zip}
          >
            <input
              id="fv2-input-zip"
              className="fv2-step4-input"
              type="text"
              inputMode="numeric"
              value={zip}
              onChange={(e) => {
                const cleaned = e.target.value.replace(/\D/g, "").slice(0, 5);
                setZip(cleaned);
                clearError("zip");
              }}
              placeholder="Enter your ZIP code"
              autoComplete="postal-code"
              maxLength={5}
              aria-invalid={!!errors.zip}
            />
          </Field>

          <label className="fv2-step4-sms">
            <input
              type="checkbox"
              checked={smsOptIn}
              onChange={(e) => setSmsOptIn(e.target.checked)}
              className="fv2-step4-sms-cb"
            />
            <span className="fv2-step4-sms-label">
              It's okay to text me about my plan options and important updates.
            </span>
            <span className="fv2-step4-sms-badge">Recommended</span>
          </label>

          <p className="v2-tcpa-disclosure" data-tf-element-role="consent-language">
            By clicking “<span data-tf-element-role="submit-text">Get My Plan Options</span>”, I agree to be contacted by{" "}
            <span data-tf-element-role="consent-advertiser-name">FamilyHealth Plans</span>, licensed insurance agents, insurance agencies, marketing partners, call centers, lead buyers, vendors, and service providers at the phone number and email I provided, including by calls, texts,{" "}
            <span data-tf-element-role="consent-grantor-waived-regulated-technologies">prerecorded/artificial voice messages, and automated dialing systems</span>, about health insurance and related products. Consent is not required to make a purchase. Message and data rates may apply. I can opt out by replying STOP or by asking not to be called.
          </p>

          <button type="submit" className="fv2-step4-submit" disabled={submitting} data-tf-element-role="submit">
            {submitting ? "Submitting..." : "Get My Plan Options"}
            <ArrowRightIcon />
          </button>

          {apiError && (
            <p className="fv2-step4-error" role="alert">
              {apiError}
            </p>
          )}

          <p className="fv2-step4-secure">
            <LockIcon /> Your information is secure and encrypted.
          </p>
        </form>
      </div>
    </FunnelLayout>
  );
}
